import { Injectable } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { ConstantsService } from './constants.service';
import { signOut } from 'aws-amplify/auth';
import { SessionService } from './session.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class IdleService {

  timedOut: boolean = false;
  idleState = 'Not started.';
  userEmail = '';
  
  constructor(
    private idle: Idle,
    private constantsService: ConstantsService,
    private sessionService: SessionService,
    private apiService: ApiService
  ) {
    this.userEmail = this.sessionService.getUsername();
  }

  checkForInactivity() {
    // sets an idle timeout of 5 seconds, for testing purposes.
    this.idle.setIdle(this.constantsService.USER_INACTIVITY_TIMEOUT * 60);
    this.idle.setTimeout(this.constantsService.USER_INACTIVITY_WARNING_TIMEOUT_IN_SECOND);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.';
        // console.log(this.idleState)
        this.reset();
    });

    this.idle.onTimeout.subscribe(() => {
        this.idleState = 'Timed out!';
        this.timedOut = true;
        // console.log(this.idleState)
        this.logout();
    });

    this.idle.onIdleStart.subscribe(() => {
        this.idleState = "You've gone idle!";
        // console.log(this.idleState)
    });

    this.idle.onTimeoutWarning.subscribe((countdown:any) => {
        this.idleState = 'You will time out in ' + countdown + ' seconds!';
        // console.log(this.idleState)
    });

    this.reset();
  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    // console.log(this.idleState)
    this.timedOut = false;
  }
async logout() {
  const userEmail = this.sessionService.getMainUsername();

  const userActivite: any = {
    user_id: userEmail,
    event_name: 'Logout'
  };

  try {
    // 1️⃣ Get Local IP (LAN or Intranet)
    userActivite.ip_address = await this.getLocalIP().catch(() => 'Unavailable');

    // 2️⃣ Get Geolocation from Browser (if allowed)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          userActivite.latitude = latitude;
          userActivite.longitude = longitude;

          // 3️⃣ Reverse-geocode coordinates → location details
          try {
            const geo: any = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            ).then((res) => res.json());

            userActivite.location_name = geo.display_name || 'Unknown';
            userActivite.city =
              geo.address?.city || geo.address?.town || geo.address?.village || 'Unknown';
            userActivite.state = geo.address?.state || 'Unknown';
            userActivite.country = geo.address?.country || 'Unknown';
          } catch (geoErr) {
            console.warn('Reverse geocode failed', geoErr);
          }

          // 🚀 Send data
          this.sendLogoutActivity(userActivite);
        },
        async (geoError) => {
          console.warn('Geolocation denied', geoError);

          // 🌍 Fallback: Use IP-based lookup if location is blocked
          const ipData: any = await this.getIPInfo();
          Object.assign(userActivite, ipData);

          this.sendLogoutActivity(userActivite);
        }
      );
    } else {
      // ❌ No geolocation support — fallback
      const ipData: any = await this.getIPInfo();
      Object.assign(userActivite, ipData);
      this.sendLogoutActivity(userActivite);
    }
  } catch (error) {
    console.error('Error in logout location fetch:', error);
    this.sendLogoutActivity(userActivite);
  }
}

/** 🔄 Send logout details to backend and finalize logout */
private sendLogoutActivity(data: any): void {
  this.apiService.userActivity(data).subscribe(
    () => {
      console.log('✅ User logout activity logged:', data);
      this.finalLogout();
    },
    (error: any) => {
      console.error('❌ Error logging logout activity', error);
      this.finalLogout();
    }
  );
}

/** 🌍 Fallback IP-based location lookup */
private async getIPInfo(): Promise<any> {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return {
      ip_address: data.ip,
      latitude: data.latitude,
      longitude: data.longitude,
      location_name: `${data.city}, ${data.region}, ${data.country_name}`,
      city: data.city,
      state: data.region,
      country: data.country_name
    };
  } catch (e) {
    console.warn('IP-based location failed', e);
    return {};
  }
}

/** 💻 Get local IP (works on intranet / LAN) */
private getLocalIP(): Promise<string> {
  return new Promise((resolve, reject) => {
    const pc = new RTCPeerConnection({ iceServers: [] });
    pc.createDataChannel('');
    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch(reject);

    pc.onicecandidate = (event) => {
      if (!event || !event.candidate) return;
      const candidate = event.candidate.candidate;
      const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
      const ipMatch = ipRegex.exec(candidate);
      if (ipMatch) {
        resolve(ipMatch[1]);
        pc.close();
      }
    };

    // Timeout fallback
    setTimeout(() => reject('Timeout'), 3000);
  });
}


  finalLogout() {
    signOut();
    this.logSession();
    sessionStorage.clear();
    this.sessionService.setUser(null);
    this.sessionService.setUsername(null);
    this.sessionService.setMainUsername(null);
    this.sessionService.setLoginTime(null);
    window.location.href = "/timedout";
  }


  logSession() {
    return new Promise((resolve, reject) => {
        if (this.sessionService.getLogSession() && this.sessionService.getLogSession().session_id) {
            let username = this.sessionService.getUsername();
            let logData = {
                user_id: username,
                log_typ: this.constantsService.ACTION_TYPE_LOGOUT,
                session_id: this.sessionService.getLogSession().session_id,
            };
            // this.logService.log(logData).subscribe(
            //     (response:any) => {
            //         resolve(null);
            //     },
            //     () => {
            //         // ignore log errors
            //         resolve(null);
            //     }
            // );
        }
    });
  }
}
